(() => {
  const productCache = new Map();
  const pickerState = new WeakMap();
  const resubmittingForms = new WeakSet();

  const normalize = (value) => String(value ?? '').trim();

  function getPickerProductUrl(picker) {
    const raw = normalize(picker?.dataset?.productUrl);
    if (!raw) return '';
    return raw.split('?')[0].replace(/\.js$/, '');
  }

  function getProductId(picker) {
    return normalize(picker?.dataset?.productId);
  }

  function getSection(picker) {
    return picker.closest('.shopify-section, dialog, featured-product-information, product-card') || document;
  }

  function getMatchingForms(picker) {
    const productId = getProductId(picker);
    const root = getSection(picker);
    const forms = Array.from(root.querySelectorAll('product-form-component'));

    if (!productId) return forms;
    return forms.filter((form) => normalize(form.dataset.productId) === productId);
  }

  function getMatchingStickyBars(picker) {
    const productId = getProductId(picker);
    const root = getSection(picker);
    return Array.from(root.querySelectorAll('sticky-add-to-cart')).filter(
      (bar) => !productId || normalize(bar.dataset.productId) === productId
    );
  }

  function getSelectedOptionMap(picker) {
    const selected = new Map();

    picker.querySelectorAll('fieldset input:checked').forEach((input) => {
      const name = normalize(input.dataset.optionName);
      if (name) selected.set(name, normalize(input.value));
    });

    picker.querySelectorAll('select').forEach((select) => {
      const option = select.selectedOptions?.[0];
      const name = normalize(option?.dataset?.optionName || select.name.replace(/^options\[|\]$/g, ''));
      if (name) selected.set(name, normalize(select.value));
    });

    picker.querySelectorAll('[data-testid="variant-option-single"]').forEach((container) => {
      const valueElement = container.querySelector('[data-testid="variant-option-single-value"]');
      const value = normalize(valueElement?.textContent);
      if (!value) return;

      const clone = container.cloneNode(true);
      clone.querySelector('[data-testid="variant-option-single-value"]')?.remove();
      const name = normalize(clone.textContent);
      if (name) selected.set(name, value);
    });

    return selected;
  }

  function getProductOptionNames(product) {
    return Array.isArray(product?.options)
      ? product.options.map((option) => normalize(typeof option === 'string' ? option : option?.name))
      : [];
  }

  function resolveVariant(product, picker) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;

    const optionNames = getProductOptionNames(product);
    const selectedMap = getSelectedOptionMap(picker);

    const selectedValues = optionNames.map((name, index) => {
      if (name && selectedMap.has(name)) return selectedMap.get(name);

      const uniqueValues = [
        ...new Set(
          variants
            .map((variant) => normalize(variant?.options?.[index]))
            .filter(Boolean)
        ),
      ];

      return uniqueValues.length === 1 ? uniqueValues[0] : '';
    });

    if (selectedValues.some((value) => !value)) return null;

    return (
      variants.find((variant) =>
        selectedValues.every((value, index) => normalize(variant?.options?.[index]) === value)
      ) || null
    );
  }

  async function loadProduct(picker) {
    const productUrl = getPickerProductUrl(picker);
    if (!productUrl) throw new Error('Missing product URL for variant resolution');

    if (!productCache.has(productUrl)) {
      const request = fetch(`${productUrl}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Product lookup failed: ${response.status}`);
          return response.json();
        })
        .then((product) => {
          productCache.set(productUrl, product);
          return product;
        })
        .catch((error) => {
          productCache.delete(productUrl);
          throw error;
        });

      productCache.set(productUrl, request);
    }

    return productCache.get(productUrl);
  }

  function setResolving(picker, resolving) {
    getMatchingForms(picker).forEach((component) => {
      component.toggleAttribute('data-ro-variant-resolving', resolving);

      component.querySelectorAll('[ref="addToCartButton"]').forEach((button) => {
        if (button instanceof HTMLButtonElement) button.disabled = resolving || button.disabled;
      });

      const accelerated = component.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (resolving) accelerated?.setAttribute('hidden', 'true');
    });
  }

  function applyVariant(picker, variant) {
    if (!variant?.id) return false;

    const variantId = normalize(variant.id);
    const available = variant.available !== false;

    getMatchingForms(picker).forEach((component) => {
      const input = component.querySelector('input[name="id"][ref="variantId"], input[name="id"]');
      if (input instanceof HTMLInputElement) {
        input.value = variantId;
        input.setAttribute('value', variantId);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      component.dataset.roResolvedVariantId = variantId;
      component.removeAttribute('data-ro-variant-resolving');

      component.querySelectorAll('[ref="addToCartButton"]').forEach((button) => {
        if (button instanceof HTMLButtonElement) button.disabled = !available;
      });

      const accelerated = component.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (available) accelerated?.removeAttribute('hidden');
      else accelerated?.setAttribute('hidden', 'true');
    });

    getMatchingStickyBars(picker).forEach((bar) => {
      bar.dataset.currentVariantId = variantId;
      bar.dataset.variantAvailable = String(available);
    });

    return true;
  }

  async function resolveAndApply(picker) {
    const state = pickerState.get(picker) || { generation: 0, pending: false };
    const generation = state.generation + 1;
    pickerState.set(picker, { generation, pending: true });
    setResolving(picker, true);

    try {
      const product = await loadProduct(picker);
      const latest = pickerState.get(picker);
      if (!latest || latest.generation !== generation) return null;

      const variant = resolveVariant(product, picker);
      if (!variant) {
        console.error('[variant-integrity] No exact variant matches the selected options.');
        return null;
      }

      applyVariant(picker, variant);
      return variant;
    } catch (error) {
      console.error('[variant-integrity] Unable to resolve the selected variant.', error);
      return null;
    } finally {
      const latest = pickerState.get(picker);
      if (latest?.generation === generation) {
        pickerState.set(picker, { generation, pending: false });
      }
    }
  }

  function findPickerForComponent(component) {
    const root = component.closest('.shopify-section, dialog, featured-product-information, product-card') || document;
    const productId = normalize(component.dataset.productId);
    const pickers = Array.from(root.querySelectorAll('variant-picker'));

    return (
      pickers.find((picker) => normalize(picker.dataset.productId) === productId) ||
      (pickers.length === 1 ? pickers[0] : null)
    );
  }

  function primePicker(picker) {
    if (!(picker instanceof HTMLElement) || picker.dataset.roVariantIntegrityBound === 'true') return;
    picker.dataset.roVariantIntegrityBound = 'true';

    const productUrl = getPickerProductUrl(picker);
    if (!productUrl) return;

    loadProduct(picker)
      .then((product) => {
        productCache.set(productUrl, product);
        return resolveAndApply(picker);
      })
      .catch((error) => {
        console.error('[variant-integrity] Initial product lookup failed.', error);
        setResolving(picker, true);
      });
  }

  function primeAll(root = document) {
    root.querySelectorAll?.('variant-picker').forEach(primePicker);
  }

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const picker = target.closest('variant-picker');
      if (!picker || !target.matches('input, select')) return;

      queueMicrotask(() => resolveAndApply(picker));
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const component = form.closest('product-form-component');
      if (!component) return;

      if (resubmittingForms.has(form)) {
        resubmittingForms.delete(form);
        return;
      }

      const picker = findPickerForComponent(component);
      if (!picker) return;

      const productUrl = getPickerProductUrl(picker);
      const cached = productCache.get(productUrl);

      if (cached && typeof cached.then !== 'function') {
        const variant = resolveVariant(cached, picker);
        if (variant && applyVariant(picker, variant)) return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      resolveAndApply(picker).then((variant) => {
        if (!variant) return;
        resubmittingForms.add(form);
        const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : undefined;
        form.requestSubmit(submitter);
      });
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const path = event.composedPath?.() || [];
      const acceleratedTarget = path.find(
        (node) =>
          node instanceof Element &&
          (node.matches('shopify-accelerated-checkout, shopify-payment-button') ||
            node.getAttribute('ref') === 'acceleratedCheckoutButtonContainer')
      );

      if (!acceleratedTarget) return;

      const component = path.find((node) => node instanceof Element && node.matches('product-form-component'));
      const productForm = component || acceleratedTarget.closest('product-form-component');
      if (!productForm) return;

      const picker = findPickerForComponent(productForm);
      if (!picker) return;

      const state = pickerState.get(picker);
      const productUrl = getPickerProductUrl(picker);
      const cached = productCache.get(productUrl);
      const variant = cached && typeof cached.then !== 'function' ? resolveVariant(cached, picker) : null;

      if (state?.pending || !variant) {
        event.preventDefault();
        event.stopImmediatePropagation();
        resolveAndApply(picker);
        return;
      }

      applyVariant(picker, variant);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => primeAll(), { once: true });
  } else {
    primeAll();
  }

  document.addEventListener('shopify:section:load', (event) => primeAll(event.target));

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('variant-picker')) primePicker(node);
        primeAll(node);
      });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
